import { api } from '../../convex/_generated/api'
import { useConvexAuth, useQuery } from 'convex/react'

const useUserPermissions = () => {
    // Gate on Convex auth, not Clerk's isSignedIn (sign-out race, see src/lib/authError.ts)
    const { isAuthenticated } = useConvexAuth()
    const currentUser = useQuery(api.users.getCurrentUser, isAuthenticated ? {} : 'skip')
    // Return `undefined` while the query is still resolving so callers (e.g. AdminLayout)
    // can distinguish "loading" from "user has no permission".
    const isLoading = currentUser === undefined
    const isAdmin = isLoading
        ? undefined
        : currentUser?.userType === "operator_42a" || currentUser?.userType === "agent_42a"
    const isOperator = isLoading ? undefined : currentUser?.userType === "operator_42a"
    const isPartnerUser = isLoading ? undefined : !!currentUser?.partnerId
    const isPartnerAdmin = isLoading ? undefined : currentUser?.partnerRole === "admin"
    // True only while a 42a operator is viewing the partner portal as a partner.
    // `isPartnerUser` / `isPartnerAdmin` are true in that state too (they read the
    // EFFECTIVE partner context), so this is what distinguishes "is a partner"
    // from "is viewing as one" — e.g. to block writes that would attach the
    // operator's own identity to the partner's data.
    const isImpersonatingPartner = isLoading ? undefined : currentUser?.isImpersonatingPartner === true
    // The user IS attached to a partner, but a 42a operator deactivated that
    // partner from /admin/partners, so the whole partner team loses the portal.
    // `isPartnerUser` is already false in that state (partnerId is the EFFECTIVE
    // partner, which the backend nulls out) — this only explains WHY, for copy.
    // Never branch access on it.
    const isPartnerAccessDisabled = isLoading ? undefined : currentUser?.partnerAccessDisabled === true
    return { isAdmin, isOperator, isPartnerUser, isPartnerAdmin, isImpersonatingPartner, isPartnerAccessDisabled }
}

export default useUserPermissions

/**
 * @deprecated Use `useUserPermissions()` instead — it now exposes
 * `isPartnerUser` and `isPartnerAdmin` derived from the current-user query,
 * without the need for an ErrorBoundary.
 */
export const usePartnerPermissions = () => {
    const { isPartnerUser, isPartnerAdmin } = useUserPermissions()
    return { isPartnerUser, isPartnerAdmin }
}
